import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { Loader2, ShieldAlert, ShieldCheck, LockOpen, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const OUTCOME_META = {
  success: { icon: ShieldCheck, color: "text-emerald-300", bg: "bg-emerald-500/15", label: "Success" },
  failure: { icon: ShieldAlert, color: "text-red-300", bg: "bg-red-500/15", label: "Failure" },
  locked: { icon: LockOpen, color: "text-amber-300", bg: "bg-amber-500/15", label: "Locked" },
  reset: { icon: KeyRound, color: "text-purple-300", bg: "bg-purple-500/15", label: "Reset" },
};

export default function SALoginActivity() {
  const { tenantId } = useSuperAdminTenant();
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    if (outcome !== "all") params.outcome = outcome;
    saApi.get("/login-activity", { params })
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tenantId, outcome]);

  return (
    <SuperAdminShell title="Login Activity">
      <div className="px-5 md:px-8 py-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger data-testid="sa-login-outcome" className="w-[180px] bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failure">Failure</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
              <SelectItem value="reset">Reset</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-white/50 ml-auto">{loading ? "…" : `${rows.length} rows`}</span>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">
                  No sign-ins recorded for this scope.
                  <div className="text-[11px] text-white/30 mt-1">Try changing the tenant filter, or check back after some activity.</div>
                </td></tr>
              ) : rows.map((r) => {
                const m = OUTCOME_META[r.outcome] || OUTCOME_META.failure;
                const Icon = m.icon;
                return (
                  <tr key={r.id || `${r.created_at}-${r.email_hash}`} data-testid="sa-login-row"
                      className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-2 text-white/70 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-cyan-300/90 text-xs">{r.tenant_name}</td>
                    <td className="px-4 py-2 text-white/80">{r.email || "—"}</td>
                    <td className="px-4 py-2 text-white/60 text-xs">{r.ip || "—"}</td>
                    <td className="px-4 py-2 text-white/60 text-xs">{r.country_name || (r.country_code === "LAN" ? "Private" : "—")}</td>
                    <td className="px-4 py-2">
                      <Badge className={`${m.bg} ${m.color} border-0`}>
                        <Icon className="w-3 h-3 mr-1 inline" />{m.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SuperAdminShell>
  );
}
